/**
 * Construction des groupes de lignes « Données contractuelles et techniques »
 * d'un compteur électrique (PDL) — logique PURE, extraite du composant pour
 * être testable (DEV-1479 S9, décisions D4 du 24/08) :
 *
 * 1. FAITS RÉELS sous leur vrai nom (leçon DEV-1324 — jamais de valeur
 *    refabriquée) : quand `teleoperable` est inconnu mais que le store porte le
 *    mode/média de relève Enedis, on affiche « Télérelevé IP » (les libellés
 *    Enedis, joints) au lieu d'un tiret muet ; en C4/C2 le comptage passe par
 *    le transformateur de courant, pas par un disjoncteur → « Calibre TC »
 *    (ex. 500/5) quand le fait est présent.
 *
 * 2. CONTRATS C ET P : quand `informations.pdl` porte les deux blocs
 *    (structure tarifaire soutirage + `injection.*`), deux sections titrées
 *    « Contrat soutirage (C) » / « Contrat injection (P) » ; un compteur pur
 *    injection (segment P4/P3/P1) n'affiche QUE la section P — construite
 *    depuis `injection.*` quand dapi le sert, sinon depuis la structure
 *    tarifaire existante (qui porte le contrat P de ces compteurs tant que la
 *    projection canonique S3 n'a pas posé le vrai sens). Aucune section vide
 *    n'est fabriquée : un compteur C pur garde l'affichage à plat d'aujourd'hui.
 */
import { InformationPDL, LineBlockLine } from "@types"

export type TechnicalGroup = {
    /** Titre de section (null = lignes à plat, comme aujourd'hui) */
    title: string | null
    lines: LineBlockLine[]
}

type Translate = (key: string) => string

function formatValue(
    value: string | number | boolean | null | undefined,
    unit?: string
): string {
    if (value == null) return "-"
    else if (unit) return `${value} ${unit}`
    else return `${value}`
}

/**
 * Libellé du branchement. dapi sert `raccordement.branchement.triphase` ;
 * `branchement_triphase` (ancien nom, jamais servi) n'est lu qu'en repli.
 * true = triphasé, false = monophasé, null/absent = non renseigné : l'absence
 * d'information ne s'affiche jamais « Monophasé ».
 */
function libelleBranchement(
    pdl: InformationPDL | null | undefined,
    t: Translate
): string {
    if (pdl?.etat === "inexistant") return "-"
    const raccordement = pdl?.compteur?.raccordement
    const triphase =
        raccordement?.branchement?.triphase ??
        raccordement?.branchement_triphase
    if (triphase === true) return t("pdl.triphase")
    if (triphase === false) return t("pdl.monophase")
    return t("pdl.non_renseigne")
}

/** Segments pur injection (production) — l'onglet était modelé soutirage. */
const SEGMENTS_INJECTION = ["P4", "P3", "P1"]

export function buildPdlTechnicalGroups(
    pdl: InformationPDL | null | undefined,
    t: Translate
): TechnicalGroup[] {
    if (!pdl) return []
    const segment = pdl?.compteur?.segment || ""

    // ---- Groupe technique (identité physique du point) ----------------------
    const technical: LineBlockLine[] = [
        {
            text: t("pdl.localisation"),
            value: formatValue(pdl?.compteur?.localisation),
            tooltip: t("pdl.tooltip.localisation"),
        },
        {
            text: t("pdl.niveau_ouverture"),
            value: formatValue(pdl?.compteur?.niveau_ouverture_services),
            tooltip: t("pdl.tooltip.niveau_ouverture"),
        },
    ]

    // Téléopérable (tri-état honnête, DEV-1324) OU le FAIT de relève quand le
    // tri-état est inconnu : « Télérelevé IP » = libellés Enedis joints, jamais
    // une valeur déduite.
    const teleoperable = pdl?.compteur?.teleoperable
    const releveFait = [
        pdl?.compteur?.releve?.mode?.libelle,
        pdl?.compteur?.releve?.media?.libelle,
    ]
        .filter(Boolean)
        .join(" ")
    if (teleoperable === true || teleoperable === false) {
        technical.push({
            text: t("pdl.teleoperable"),
            value: teleoperable
                ? t("pdl.teleoperable_yes")
                : t("pdl.teleoperable_no"),
            tooltip: t("pdl.tooltip.teleoperable"),
        })
    } else if (releveFait) {
        technical.push({
            text: t("pdl.releve"),
            value: releveFait,
            tooltip: t("pdl.tooltip.releve"),
        })
    } else {
        technical.push({
            text: t("pdl.teleoperable"),
            value: "-",
            tooltip: t("pdl.tooltip.teleoperable"),
        })
    }

    // Calibre : en C4/C2 le fait pertinent est le calibre du transformateur de
    // courant quand il est présent ; sinon la ligne disjoncteur d'aujourd'hui.
    const calibreTc = pdl?.compteur?.transformateur_courant?.calibre
    if (["C4", "C2"].includes(segment) && calibreTc) {
        technical.push({
            text: t("pdl.calibre_tc"),
            value: formatValue(calibreTc),
            tooltip: t("pdl.tooltip.calibre_tc"),
        })
    } else {
        technical.push({
            text: t("pdl.calibre"),
            value: formatValue(pdl?.compteur?.calibre_disjoncteur, t("a")),
        })
    }

    technical.push(
        {
            text: t("pdl.tension"),
            value: formatValue(pdl?.compteur?.tension_livraison),
        },
        {
            text: t("pdl.domaine_tension"),
            value: formatValue(pdl?.compteur?.domaine_tension),
        },
        {
            text: t("pdl.puiss_soutirage"),
            value: formatValue(
                pdl?.compteur?.raccordement?.puissance_raccordement_soutirage,
                t("kva")
            ),
        }
    )
    if (["C5", "P4"].includes(segment)) {
        technical.push({
            text: t("pdl.branchement_triphase"),
            value: libelleBranchement(pdl, t),
        })
    }

    // ---- Lignes de contrat --------------------------------------------------
    const contractLinesFromStructureTarifaire = (): LineBlockLine[] => {
        const st = pdl?.structure_tarifaire
        const lines: LineBlockLine[] = [
            {
                text: t("pdl.calendrier"),
                value: formatValue(st?.calendrier_fournisseur?.libelle),
            },
            {
                text: t("pdl.fta"),
                value: formatValue(st?.formule_tarifaire_acheminement?.fta),
                tooltip: t("pdl.tooltip.fta"),
            },
        ]
        if (st?.puissance_souscrite_soutirage?.puissance) {
            lines.push({
                text: t("pdl.puiss_souscrite"),
                value: formatValue(
                    st?.puissance_souscrite_soutirage?.puissance,
                    t("kva")
                ),
            })
        }
        if (st?.puissance_souscrite_soutirage?.denivele) {
            lines.push({
                id: "denivele",
                text: t("pdl.denivele"),
                value: st?.puissance_souscrite_soutirage?.denivele,
                tooltip: t("pdl.tooltip.denivele"),
            })
        }
        return lines
    }

    const contractLinesFromInjection = (): LineBlockLine[] => {
        const stInj = pdl?.injection?.structure_tarifaire
        const lines: LineBlockLine[] = [
            {
                text: t("pdl.fta"),
                value: formatValue(stInj?.formule_tarifaire_acheminement?.fta),
                tooltip: t("pdl.tooltip.fta"),
            },
        ]
        if (stInj?.puissance_souscrite_injection?.puissance) {
            lines.push({
                text: t("pdl.puiss_souscrite"),
                value: formatValue(
                    stInj?.puissance_souscrite_injection?.puissance,
                    t("kva")
                ),
            })
        }
        if (stInj?.puissance_souscrite_injection?.denivele) {
            lines.push({
                id: "denivele",
                text: t("pdl.denivele"),
                value: stInj?.puissance_souscrite_injection?.denivele,
                tooltip: t("pdl.tooltip.denivele"),
            })
        }
        return lines
    }

    const injection = pdl?.injection ?? null
    const groups: TechnicalGroup[] = [{ title: null, lines: technical }]

    if (SEGMENTS_INJECTION.includes(segment)) {
        // Pur producteur : la section P seule. `injection.*` (dapi) fait foi
        // quand il est servi ; sinon la structure tarifaire existante porte le
        // contrat P de ces compteurs (état pré-S3, divergence consignée DEV-1479).
        groups.push({
            title: t("pdl.section_injection"),
            lines: injection
                ? contractLinesFromInjection()
                : contractLinesFromStructureTarifaire(),
        })
    } else if (injection) {
        // Les deux contrats : deux sections clairement titrées.
        groups.push({
            title: t("pdl.section_soutirage"),
            lines: contractLinesFromStructureTarifaire(),
        })
        groups.push({
            title: t("pdl.section_injection"),
            lines: contractLinesFromInjection(),
        })
    } else {
        // Compteur C pur : affichage à plat, comme aujourd'hui.
        groups.push({
            title: null,
            lines: contractLinesFromStructureTarifaire(),
        })
    }
    return groups
}
